// Debug status component for API diagnostics

import { apiUrl } from '../utils/api.js';
import { fetchHealth } from '../utils/health.js';

export const debugStatus = () => ({
  // State
  apiInfo: {
    VITE_API_URL: import.meta.env.VITE_API_URL,
    VITE_DEBUG_API: import.meta.env.VITE_DEBUG_API,
    PROD: import.meta.env.PROD,
    DEV: import.meta.env.DEV,
  },
  
  healthResults: {
    baseHealth: null,
    apiHealth: null,
    testing: false
  },
  
  debugCalls: [],
  
  // Initialize component
  init() {
    console.log('🔧 Debug Status component initialized');
    this.refreshDebugCalls();
    
    // Refresh calls every 2 seconds
    setInterval(() => {
      this.refreshDebugCalls();
    }, 2000);
  },
  
  // Refresh debug calls from window.__DBG__
  refreshDebugCalls() {
    this.debugCalls = window.__DBG__?.calls || [];
  },
  
  // Get API configuration info
  getApiConfig() {
    const RAW = import.meta.env.VITE_API_URL || "";
    const BASE = RAW.replace(/\/+$/, "");
    const HAS_API_SUFFIX = /\/api$/i.test(BASE);
    const API_PREFIX = HAS_API_SUFFIX ? "" : "/api";
    
    return {
      RAW,
      BASE, 
      API_PREFIX,
      HAS_API_SUFFIX,
      currentUrl: window.location.href,
      userAgent: navigator.userAgent.split(' ').pop()
    };
  },
  
  // Ping API health endpoints
  async pingAPI() {
    this.healthResults.testing = true;
    this.healthResults.baseHealth = null;
    this.healthResults.apiHealth = null;
    
    try {
      // Test unified health endpoint (GET only)
      const baseStart = Date.now();
      const baseData = await fetchHealth();
      const baseTime = Date.now() - baseStart;
      
      this.healthResults.baseHealth = {
        status: 200,
        ok: true,
        data: baseData,
        responseTime: baseTime,
        url: apiUrl('/health')
      };
    } catch (error) {
      this.healthResults.baseHealth = {
        status: 0,
        ok: false,
        error: error.message,
        url: apiUrl('/health')
      };
    }
    
    try {
      // Test alias endpoint (also unified GET)
      const apiStart = Date.now();
      const apiData = await fetchHealth();
      const apiTime = Date.now() - apiStart;
      
      this.healthResults.apiHealth = {
        status: 200,
        ok: true,
        data: apiData,
        responseTime: apiTime,
        url: apiUrl('/health')
      };
    } catch (error) {
      this.healthResults.apiHealth = {
        status: 0,
        ok: false,
        error: error.message,
        url: apiUrl('/health')
      };
    }
    
    this.healthResults.testing = false;
  },
  
  // Clear debug calls
  clearDebugCalls() {
    if (window.__DBG__) {
      window.__DBG__.calls = [];
    }
    this.refreshDebugCalls();
  },
  
  // Test different apiUrl combinations
  testApiUrl() {
    const tests = [
      '/auth/login',
      'auth/login', 
      '/api/auth/login',
      'api/auth/login',
      'https://example.com/api/test',
      '/api/missions',
      '/travel/cities'
    ];
    
    console.group('🧪 apiUrl() Tests');
    tests.forEach(test => {
      const result = apiUrl(test);
      console.log(`apiUrl("${test}") → "${result}"`);
    });
    console.groupEnd();
    
    // Show toast notification
    window.dispatchEvent(new CustomEvent('toast', {
      detail: {
        type: 'info',
        text: 'Ver console para resultados de apiUrl() tests'
      }
    }));
  },
  
  // Format JSON for display
  formatJSON(obj) {
    return JSON.stringify(obj, null, 2);
  },
  
  // Get status color class
  getStatusColor(result) {
    if (!result) return 'text-gray-400';
    if (result.ok) return 'text-green-400';
    if (result.status >= 400) return 'text-red-400';
    return 'text-yellow-400';
  },
  
  // Copy to clipboard
  async copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
      window.dispatchEvent(new CustomEvent('toast', {
        detail: {
          type: 'success', 
          text: 'Copiado al portapapeles'
        }
      }));
    } catch (error) {
      console.error('Failed to copy:', error);
      window.dispatchEvent(new CustomEvent('toast', {
        detail: {
          type: 'error',
          text: 'Error al copiar'
        }
      }));
    }
  }
});